import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  getMe,
  updateMe,
} from '../api/auth'
import type { AuthUser } from '../api/auth'
import { track, identify, resetAnalytics, AnalyticsEvent } from '../lib/analytics'
import { authArrivalEvent, type AuthArrival } from '../lib/authArrival'
import { Sentry, setSentryUser, clearSentryUser } from '../lib/sentry'
import { fullSync, syncFromServer } from '../lib/sync'
import { clearDatabase } from '../lib/db'
import { WELCOME_INTRO_PLAYED_KEY } from '../lib/onboarding'
import { WHATS_NEW_SEEN_KEY } from '../lib/whatsNew'
import { clearTabClientId } from '../lib/syncDevice'
import { clearInflightRecords } from '../lib/inflightRecords'
import {
  clearPreviousOwner,
  getPreviousOwner,
  rememberPreviousOwner,
  resolveSignInOwnership,
  type PreviousOwner,
} from '../lib/previousOwner'
import { useUIStore } from './ui'

/**
 * Local mode: the user chose "Start writing" without an account. Notes live
 * only in the local DB; no server row exists. Persisted under its own key
 * (not inside `caliu-auth`) so the engineering state stays a plain flag.
 */
export const LOCAL_MODE_KEY = 'caliu-local-mode'

function readLocalModeFlag(): boolean {
  try {
    return localStorage.getItem(LOCAL_MODE_KEY) === 'true'
  } catch {
    return false
  }
}

function writeLocalModeFlag(on: boolean): void {
  try {
    if (on) localStorage.setItem(LOCAL_MODE_KEY, 'true')
    else localStorage.removeItem(LOCAL_MODE_KEY)
  } catch {
    // Storage unavailable — the in-memory flag still governs this session.
  }
}

interface AuthState {
  user: AuthUser | null
  loading: boolean
  /** True when the user writes without any account (notes on this device only). */
  localMode: boolean
  /** Enter local mode (front-door onboarding completed without an account). */
  enterLocalMode: () => void
  setUser: (user: AuthUser | null) => void
  /** Update the current user's display name (pass null to clear). */
  updateName: (name: string | null) => Promise<void>
  login: (
    email: string,
    password: string,
    opts?: { discardLocalNotes?: boolean },
  ) => Promise<void>
  register: (
    email: string,
    password: string,
    name?: string,
    next?: string | null,
    locale?: string,
  ) => Promise<{ ok: boolean; message: string }>
  /**
   * Sign out. The default KEEPS the notes on this device and lands in local
   * mode (they are still readable, editable and exportable, and signing back
   * in reconciles them); `keepNotes: false` is the old behavior, a full wipe
   * for handing the device to someone else.
   */
  logout: (options?: { keepNotes?: boolean }) => Promise<void>
  /** The account whose notes are parked on this device after a keep-logout. */
  previousOwner: PreviousOwner | null
  /** Force-logout on 401 — skips sync (session is dead) and clears local state. */
  forceLogout: () => Promise<void>
  checkAuth: () => Promise<void>
}

/**
 * Wipe all localStorage on sign-out so the next user on this browser inherits
 * nothing: onboarding flag, theme/font/language prefs, device id, sort prefs.
 * Called before `set({ user: null })` so zustand persist re-writes `caliu-auth`
 * (with a null user) after the wipe.
 */
function wipeLocalStorage(): void {
  try {
    // Per browser, not per account: the next tour skips the welcome intro.
    const introPlayed = localStorage.getItem(WELCOME_INTRO_PLAYED_KEY)
    // Same for "What's new": a release this browser was shown stays shown.
    const whatsNewSeen = localStorage.getItem(WHATS_NEW_SEEN_KEY)
    localStorage.clear()
    if (introPlayed) localStorage.setItem(WELCOME_INTRO_PLAYED_KEY, introPlayed)
    if (whatsNewSeen) localStorage.setItem(WHATS_NEW_SEEN_KEY, whatsNewSeen)
    // The seq counter just went: this tab's content sync client id goes with it.
    clearTabClientId()
  } catch {
    // Storage unavailable (private mode edge cases) — nothing to wipe.
  }
}

/**
 * Destroy every trace of the signed-out account on this device: local DB,
 * localStorage, and the theme override. Only ever call this when a
 * server-backed account was active — for a user with no server copy (local
 * mode, or a stray 401 with no session) this would be silent total data loss.
 */
async function wipeLocalAccountState(): Promise<void> {
  // Wipe local DB before clearing user to prevent race with re-login
  await clearDatabase()
  // The changes in flight belong to the database that just went.
  await clearInflightRecords().catch((err: unknown) =>
    Sentry.captureException(err, { tags: { operation: 'inflight_records_clear' } }),
  )
  wipeLocalStorage()
  // Reset theme to default (also re-persists default keys post-wipe)
  const { setTheme, setColorTheme } = useUIStore.getState()
  setTheme('system')
  setColorTheme('caliu')
}

/**
 * An arrival that has been read out of the URL and sessionStorage but not yet
 * reported. Held here so a `getMe()` that fails transiently right after an
 * OAuth redirect does not swallow the signup: the markers are one-shot, and
 * once stripped there is nothing for the retry to read.
 */
let pendingArrival: AuthArrival | null = null

/**
 * Read the markers this arrival carries, clearing them from the page as we go.
 *
 * `provider` is the breadcrumb `GoogleSignInButton` leaves in sessionStorage
 * before handing the tab to Google (an OAuth round-trip carries no other
 * signal). `isNewUser` and `signUpMethod` come from the redirect the worker
 * built when this exchange created the account — email verification and Google
 * alike. The URL params are dropped via `replaceState` so a refresh does not
 * re-report the signup, and anything worth reporting is parked in
 * `pendingArrival` until it actually is. What to do with it is
 * `authArrivalEvent`.
 */
function consumeAuthArrival(): AuthArrival {
  if (pendingArrival) return pendingArrival

  let provider: string | null = null
  try {
    provider = sessionStorage.getItem('caliu-pending-oauth')
    if (provider) sessionStorage.removeItem('caliu-pending-oauth')
  } catch {
    // Storage unavailable (private mode) — skip attribution.
  }

  let isNewUser = false
  let signUpMethod: string | null = null
  try {
    const url = new URL(window.location.href)
    isNewUser = url.searchParams.get('is_new_user') === '1'
    signUpMethod = url.searchParams.get('signup_method')
    if (isNewUser || signUpMethod) {
      url.searchParams.delete('is_new_user')
      url.searchParams.delete('signup_method')
      window.history.replaceState({}, '', url.toString())
    }
  } catch {
    // No URL/history access (non-browser test env) — treat as a plain login.
  }

  const arrival: AuthArrival = { provider, isNewUser, signUpMethod }
  // Only worth holding on to if there is something to report.
  pendingArrival = provider || isNewUser ? arrival : null
  return arrival
}

/** Mixpanel profile props (PII lives on the profile, never on events). */
function profileFor(user: AuthUser): Record<string, unknown> {
  const profile: Record<string, unknown> = {}
  // Sign in with Apple can withhold the address — never write a null $email.
  if (user.email) profile.$email = user.email
  if (user.name) profile.$name = user.name
  return profile
}

/** Identify + keep the segmentation props fresh (never event props). */
function identifyUser(user: AuthUser): void {
  identify(user.id, profileFor(user))
}

/**
 * Settle who the local rows belong to, the moment a sign-in resolves and
 * BEFORE any sync runs. A different account than the one that parked them
 * gets a clean device (their attachment URLs and note links belong to the old
 * account — see lib/previousOwner); the same account keeps everything.
 *
 * Must be awaited before `syncFromServer`, or the pull/push could carry the
 * old account's rows into the new one.
 */
async function settleLocalRowOwnership(signedInUserId: string): Promise<void> {
  const marker = getPreviousOwner()
  if (!marker) return
  if (resolveSignInOwnership(marker, signedInUserId) === 'wipe') {
    await clearDatabase()
  }
  clearPreviousOwner()
}

/**
 * Upload the attachments written before this account existed.
 *
 * Without an account `POST /attachments` is a guaranteed 401, so every image
 * added in local mode is sitting in `image_upload_queue` (some of them parked
 * as `failed` by an older build, or by a storage cap). Signing in is the first
 * moment they can go anywhere, and doing it here rather than waiting for the
 * focus handler is what stops the first cross-device open from showing a note
 * full of 404s.
 *
 * Best-effort by construction: the queue is durable, so a failure here just
 * means the next sync tick tries again. The dynamic import breaks the cycle
 * back into this store (`imageSync` reads `useAuthStore`).
 */
async function drainLocalAttachments(): Promise<void> {
  try {
    const [{ retryFailedImageUploads }, { processImageUploadQueue }] = await Promise.all([
      import('../lib/db'),
      import('../lib/imageSync'),
    ])
    retryFailedImageUploads()
    await processImageUploadQueue()
  } catch (err) {
    Sentry.captureException(err)
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      loading: false,
      localMode: readLocalModeFlag(),
      previousOwner: getPreviousOwner(),

      enterLocalMode: () => {
        if (get().localMode) return
        writeLocalModeFlag(true)
        set({ localMode: true })
        track(AnalyticsEvent.LocalModeStarted)
        // Best-effort: ask the browser not to evict our storage. Local mode
        // has no server copy, so eviction would be total data loss.
        try {
          void navigator.storage?.persist?.()
        } catch {
          // Not supported — nothing else to do.
        }
      },

      setUser: (user) => set({ user }),

      updateName: async (name) => {
        const updated = await updateMe(name)
        set({ user: updated })
        setSentryUser(updated)
        // Keep the Mixpanel profile name in sync (PII lives on the profile only).
        identifyUser(updated)
      },

      login: async (email, password, { discardLocalNotes = false } = {}) => {
        const user = await apiLogin(email, password)
        // The user asked for this device's local notes to be deleted rather
        // than merged (LoginPage dialog). It happens HERE, not at the dialog:
        // deleting before the credentials are known good turns a mistyped
        // password into permanent note loss, and deleting after the sync below
        // would be too late (the rows would already have been pushed up, and
        // the next pull would bring them back).
        if (discardLocalNotes) await clearDatabase()
        // Leaving local mode by signing in: the merge/discard decision over any
        // existing local notes happens BEFORE this call (LoginPage dialog).
        // Notes parked by a keep-logout are settled here instead, because only
        // now do we know whether this is the account that owns them.
        await settleLocalRowOwnership(user.id)
        writeLocalModeFlag(false)
        // Pull notes & tags from server so IDB is populated before the UI mounts.
        // Best-effort: don't block login if sync fails (e.g. flaky connection).
        try {
          await syncFromServer()
        } catch {
          // Sync will retry later via useSync
        }
        set({ user, localMode: false, previousOwner: null })
        void drainLocalAttachments()
        setSentryUser(user)
        // Identify before tracking so the event is attributed to the user.
        // PII (email/name) goes to the profile, never to event properties.
        identifyUser(user)
        track(AnalyticsEvent.UserLoggedIn, { provider: 'password' })
      },

      register: async (email, password, name, next, locale) => {
        // No `sign_up_completed` here: this only queues a verification email,
        // and the account does not exist until that link is opened. The event
        // fires in `checkAuth` off the `is_new_user` marker the server puts on
        // the post-verification redirect.
        return await apiRegister(email, password, name, next, locale)
      },

      logout: async ({ keepNotes = true } = {}) => {
        // No server-backed account — nothing to log out of, and wiping would
        // destroy the only copy of the user's local data.
        const current = get().user
        if (!current) return
        set({ loading: true })
        try {
          // Flush pending changes to server before signing out
          await fullSync()
        } catch {
          // Best-effort — proceed with logout even if sync fails. On the keep
          // path an unflushed change is not lost either: it stays queued on
          // this device until the account signs back in.
        }
        try {
          await apiLogout()
        } finally {
          track(AnalyticsEvent.UserLoggedOut, { kept_notes: keepNotes })
          resetAnalytics()
          clearSentryUser()
          // A real sign-out ends any pending switch: a stale marker would
          // send the NEXT sign-in wiping a library it has nothing to do with.
          if (keepNotes) {
            // Land in local mode with everything intact. The rows keep this
            // account's note ids on purpose — see lib/previousOwner.
            const owner: PreviousOwner = { userId: current.id, email: current.email }
            rememberPreviousOwner(owner)
            writeLocalModeFlag(true)
            set({ user: null, localMode: true, previousOwner: owner, loading: false })
          } else {
            clearPreviousOwner()
            await wipeLocalAccountState()
            set({ user: null, previousOwner: null, loading: false })
          }
        }
      },

      forceLogout: async () => {
        // No server-backed account (already logged out via concurrent 401s, or
        // a stray 401 with no session) — never wipe: local data may be the
        // only copy.
        const current = get().user
        if (!current) return
        track(AnalyticsEvent.UserSessionExpired)
        resetAnalytics()
        clearSentryUser()
        // Park, don't wipe. Nobody chose this exit: the session expired, or
        // another device revoked it. Wiping here destroys work that never
        // reached the server, and it contradicts what the Devices screen
        // promises ("unsynced changes stay there until they sign in again").
        // Signing back in to the same account keeps the rows; signing in to a
        // different one hands them to the existing parked-notes gate.
        const owner: PreviousOwner = { userId: current.id, email: current.email }
        rememberPreviousOwner(owner)
        writeLocalModeFlag(true)
        set({ user: null, localMode: true, previousOwner: owner, loading: false })
      },

      checkAuth: async () => {
        set({ loading: true })

        // Offline: trust the persisted user from localStorage (zustand persist).
        // The store is rehydrated automatically — no need to hit the API.
        if (!navigator.onLine) {
          const persisted = get().user
          if (persisted) setSentryUser(persisted)
          set({ loading: false })
          return
        }

        // Read (and consume) the redirect markers BEFORE the first await, so a
        // second `checkAuth` racing this one — or a plain page refresh — cannot
        // report the same arrival twice.
        const arrival = consumeAuthArrival()

        try {
          const user = await getMe()
          // A live session (e.g. returning from a Google OAuth redirect while
          // in local mode) supersedes local mode. Settle parked rows first:
          // an OAuth round trip carries no identity until it lands, so this
          // is the earliest point where a different account can be told apart
          // from the one that parked them.
          if (user) {
            await settleLocalRowOwnership(user.id)
            writeLocalModeFlag(false)
          } else if (get().user) {
            // A definitive 401 for an account this device still holds: the
            // session expired or was revoked elsewhere. Park exactly like
            // `forceLogout` does. Writing `user: null` alone left the rows
            // ownerless, and the next sign-in adopted them as its own.
            await get().forceLogout()
            return
          }
          set(
            user
              ? { user, loading: false, localMode: false, previousOwner: null }
              : { user, loading: false },
          )
          if (user) {
            setSentryUser(user)
            // Re-identify on app re-open while already logged in (session
            // restore) so events keep linking to the same Mixpanel profile.
            identifyUser(user)
            // A signup and a login come back through the same redirect, so
            // the decision lives in one pure place (mirrored on Swift's
            // `trackAuthCompletion`). They are mutually exclusive.
            const arrivalEvent = authArrivalEvent(arrival)
            if (arrivalEvent?.event === 'sign_up_completed') {
              track(AnalyticsEvent.SignUpCompleted, { method: arrivalEvent.method })
            } else if (arrivalEvent?.event === 'user_logged_in') {
              track(AnalyticsEvent.UserLoggedIn, { provider: arrivalEvent.provider })
            }
            // Reported (or nothing to report) — release it so a later
            // `checkAuth` in this page session cannot fire it a second time.
            pendingArrival = null
            // Covers the redirect arrivals login() never sees (Google OAuth,
            // email verification): the local-mode attachments finally have an
            // account to upload to.
            void drainLocalAttachments()
          }
          // Pull notes from server after confirming the session.
          // Handles first-time PWA installs where IndexedDB is empty but a valid
          // server session exists (e.g. user adds to home screen on a new device).
          // Mirrors what login() does — best-effort, useSync will retry on failure.
          try {
            await syncFromServer()
          } catch {
            // Sync will retry via useSync polling
          }
        } catch {
          // getMe only returns null on a definitive 401/403 — anything that
          // lands here is transient (offline mid-request, 5xx). Keep trusting
          // the persisted user: a flaky server must never read as a sign-out.
          set({ loading: false })
        }
      },
    }),
    {
      name: 'caliu-auth',
      // Only persist user, not loading state
      partialize: (state) => ({ user: state.user }),
    },
  ),
)
